import React from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Tag} from "primereact/tag";

import {actionLabel, changeSummary, fieldLabel, formatNoteValue, formatValue, noteLabel,
    scopeLabel, scopeSeverity} from "./dynamicPricingAuditLabels";

// Shared rendering for a page of dynamic pricing audit records: the same
// columns (when / who / level / what) and the same per-field expansion row,
// used by both the campaign-wide dialog (DynamicPricingAuditDialog) and the
// product detail history list (ProductDynamicPricingAuditHistory). Lifted
// out so a record can never render two different ways depending on which
// screen shows it -- callers own filtering and paging state, this owns only
// how a page of rows is displayed.

// Bulk operations carry no per-field diff; they list the products they hit.
const auditRowExpansion = (t) => (row) => (
    <div style={{padding: '8px 12px'}}>
        {(row.changes || []).length > 0 && (
            <table style={{borderCollapse: 'collapse', fontSize: '.85rem'}}>
                <thead>
                    <tr style={{color: '#6b7280'}}>
                        <th style={{textAlign: 'left', padding: '4px 12px 4px 0'}}>{t('dynamic_pricing_audit.column_field')}</th>
                        <th style={{textAlign: 'left', padding: '4px 12px 4px 0'}}>{t('dynamic_pricing_audit.before')}</th>
                        <th style={{textAlign: 'left', padding: '4px 0'}}>{t('dynamic_pricing_audit.after')}</th>
                    </tr>
                </thead>
                <tbody>
                    {row.changes.map(change => (
                        <tr key={change.field}>
                            <td style={{padding: '4px 12px 4px 0'}}>{fieldLabel(t, change.field)}</td>
                            <td style={{padding: '4px 12px 4px 0', color: '#dc2626'}}>
                                {formatValue(t, change.field, change.before)}</td>
                            <td style={{padding: '4px 0', color: '#16a34a'}}>
                                {formatValue(t, change.field, change.after)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
        {(row.affected_sample || []).length > 0 && (
            <div style={{marginTop: 8, fontSize: '.8rem', color: '#6b7280'}}>
                <div style={{fontWeight: 600, marginBottom: 4}}>
                    {t('dynamic_pricing_audit.affected', {count: row.affected_count || 0})}
                </div>
                {row.affected_sample.map(item => item.label).join(', ')}
                {row.affected_count > row.affected_sample.length && ' …'}
            </div>
        )}
        {Object.keys(row.notes || {}).length > 0 && (
            <div style={{marginTop: 8, fontSize: '.8rem', color: '#6b7280'}}>
                {Object.entries(row.notes).map(([key, value]) =>
                    noteLabel(t, key) + ': ' + formatNoteValue(t, value)).join(' · ')}
            </div>
        )}
    </div>
);

const DynamicPricingAuditTable = ({t, rows, loading, first, pageSize, total, onPage,
                                    expandedRows, onRowToggle, emptyMessage}) => (
    <DataTable value={rows} loading={loading} lazy paginator
               first={first} rows={pageSize} totalRecords={total}
               onPage={onPage}
               expandedRows={expandedRows} onRowToggle={onRowToggle}
               rowExpansionTemplate={auditRowExpansion(t)}
               emptyMessage={emptyMessage}>
        <Column expander style={{width: '3em'}}/>
        <Column header={t('dynamic_pricing_audit.when')} style={{width: '12em'}}
                body={(row) => new Date(row.created_at).toLocaleString()}/>
        <Column header={t('dynamic_pricing_audit.who')} style={{width: '14em'}}
                body={(row) => (
                    <span>
                        {row.actor_name || row.actor_email}
                        {row.is_impersonated && <span style={{marginLeft: 6, fontSize: '.7rem',
                            color: '#b45309', border: '1px solid #fcd34d', borderRadius: 4,
                            padding: '0 4px'}}>{t('dynamic_pricing_audit.impersonated')}</span>}
                    </span>
                )}/>
        <Column header={t('dynamic_pricing_audit.level')} style={{width: '8em'}}
                body={(row) => <Tag value={scopeLabel(t, row.scope)} severity={scopeSeverity(row.scope)}/>}/>
        <Column header={t('dynamic_pricing_audit.what')}
                body={(row) => (
                    <div>
                        <div>{actionLabel(t, row)}{row.target_label ? ' · ' + row.target_label : ''}</div>
                        <small style={{color: '#6b7280'}}>{changeSummary(t, row)}</small>
                    </div>
                )}/>
    </DataTable>
);

export default DynamicPricingAuditTable;
